import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, SerializationContext, SerializedData, isSameData } from '../../models/dto/dto.js';
import { PhotoReference } from '../../models/serializable/file-reference/photo-reference.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class InvoiceSettingsComponent extends Component {
  static readonly TYPE_ID: string = '5500e8f4fdc944b0b10ff12faebce135';
  static readonly SCHEMA_VERSION: number = 4;

  constructor(
    public readonly organizationLogo: PhotoReference | null,
    public readonly eventRules: string,
    public readonly organizationDetails: string,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return InvoiceSettingsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(InvoiceSettingsComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): InvoiceSettingsComponent {
    return new InvoiceSettingsComponent(
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'organizationLogo')),
        MapUtils.get<string>(map, 'eventRules'),
        MapUtils.get<string>(map, 'organizationDetails'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'organizationLogo': context.toDataOrNull(this.organizationLogo),
      'eventRules': this.eventRules,
      'organizationDetails': this.organizationDetails,
    };
  }

  clone(): this {
    return new InvoiceSettingsComponent(
        this.organizationLogo?.clone() ?? null,
        this.eventRules,
        this.organizationDetails,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.organizationLogo, this.organizationLogo) &&
        other.eventRules == this.eventRules &&
        other.organizationDetails == this.organizationDetails;
  }
}

export class InvoiceSettingsComponentBuilder extends DataModelBuilder<InvoiceSettingsComponent> {
  build(context: SerializationContext, map: SerializedData): InvoiceSettingsComponent {
    return InvoiceSettingsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(InvoiceSettingsComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(InvoiceSettingsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the `organizationLogo` parameter of type `PhotoReference | null` was introduced.
    if(version < 2) {
      version = 2;
    }

    // In this version the `importantInfo` and `organizationDetails` parameters
    // of type `string` were introduced.
    if(version < 3) {
      map['importantInfo'] ??= '';
      map['organizationDetails'] ??= '';
      version = 3;
    }

    // In this version `importantInfo` was renamed to `eventRules`.
    if(version < 4) {
      map['eventRules'] ??= map['importantInfo'];
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
